import { useState } from "react";
import { useOutsideClick } from "src/Hooks";
import { EQActionType, EQStateType } from "src/Views/EQueue";
import style from "./ReturnWidget.module.scss";
import classNames from "classnames";
import { Select2WebElementType } from "src/Types/ReDoc";
import { ReactComponent as TimerReTurnIcon } from '../../Assets/ReTurnQueue/Timer.svg';
import { ReactComponent as InfoReTurnIcon } from '../../Assets/ReTurnQueue/infoReTurn.svg';

interface IReturnWidgetProps {
	// Состояние виджета
	state: EQStateType;
	// Функция вызывается при клике на кнопки справа
	onAction: (action: string, payload?: unknown) => void;
	// Функция вызывается при закрытии виджета
	onClose: () => void;
	// Номер талона
	ticketNumber?: string;
	// Описание талона
	ticketDescription?: string;
	// Количество людей в очереди
	queueCount: number;
	// Окна для перенаправления посетителя
	windows?: Select2WebElementType[];
	// Статус загрузки окон для перенаправления посетителя
	windowsStatus?: "idle" | "loading" | "loaded";
	// Кнопки действия состояния
	actions?: EQActionType[];
	restTime?: string | boolean;
	comment?: string;
}

const ReturnWidget: React.FC<IReturnWidgetProps> = ({
	state,
	onAction,
	onClose,
	ticketNumber,
	ticketDescription,
	queueCount,
	windows,
	windowsStatus = "idle",
	restTime,
	actions,
	comment
}) => {
	const widgetRef = useOutsideClick<HTMLDivElement>(onClose);
	const [ isAutoCall, setIsAutoCall ] = useState<boolean>(true);
	const [ expandedAction, setExpandedAction ] = useState<string | null>(null);
	const [ showRedirectList, setShowRedirectList ] = useState<boolean>(false);

	const title = ticketNumber || `${queueCount} человек`;
	const description = ticketDescription || (ticketNumber ? "" : "В очереди");
	// Окна для перенаправления посетителя
	const windowOptions = (windows || []).map((w) => ({ id: w.id, title: w.text || w.id }));
	
	/** Вызывается при клике на кнопки справа */
	const onActionClick = ( actionName: string ) => {
		if ( actionName === "return-redirect" ) {
			onAction( actionName );
			setShowRedirectList( prev => !prev );
			return;
		}
		setShowRedirectList(false);
		onAction( actionName );
	};

	/** Вызывается при клике на элемент дропдауна в кнопках справа */
	const onSubActionClick = ( actionName: string ) => {
		setExpandedAction(null);
		setShowRedirectList(false);
		onAction(actionName);
	};

	return (
		<div ref={widgetRef} className={style.returnEqWidget} data-return-widget="true">
			<div className={style.returnEqWidgetHeader } style={{ borderTop: `12px solid ${state.borderTopColor || state.color}` }}>
				{/* бабл виджета со статусом */}
				<h4
					className={classNames("body-small", style.returnEqWidgetHeaderTitle)}
					style={{
						color: state.borderTopColor || state.color,
						background: state.badgeBackgroundColor
					}}
				>
					{state.text || "re:turn"}
				</h4>
				{/* свитчер автовызова */}
				<div className={style.returnEqWidgetSwitchRow}>
					<span className={classNames("body-xsmall", style.returnEqWidgetSwitchLabel)}>Автовызов</span>
					<button
						type="button"
						role="switch"
						aria-checked={isAutoCall}
						className={classNames(style.returnEqWidgetSwitch, {
							[ style.returnEqWidgetSwitchChecked ]: isAutoCall
						})}
						onClick={() => setIsAutoCall((prev) => !prev)}
					>
						<span className={style.returnEqWidgetSwitchThumb} />
					</button>
				</div>
			</div>

			<div className={style.returnEqWidgetBody}>
				<div className={style.returnEqWidgetBodyLeft}> 
					<div className={style.returnEqWidgetBodyLeftMain}>
						<h3 className={classNames("body-large", style.returnEqWidgetBodyLeftTitle)}>{title}</h3>
						{restTime && <div className={classNames('body-xsmall', style.returnEqWidgetBodyLeftTimer)}>
							<TimerReTurnIcon/>
							{restTime}
						</div>}
					</div>
					<p className={classNames(ticketDescription ? "body-xsmall" : style.returnEqWidgetBodyLeftDescBig, style.returnEqWidgetBodyLeftDesc)}>{description}</p>
					{ comment && <div className={classNames("body-xsmall", style.returnEqWidgetBodyLeftComment)}>
						<InfoReTurnIcon/>
						<p>{comment}</p>
					</div>}
				</div>
				<div className={style.returnEqWidgetBodyRight}>
					{(actions || []).map((action, index) => {
						const ActionIcon = action.icon;
						return (
						<div key={action.name} className={style.returnEqWidgetActionContainer}>
							{/* Основная кнопка действия состояния */}
							<button
								type="button"
								disabled={action.disabled}
								className={classNames(
									"body-xsmall",
									style.returnEqWidgetAction,
									index === 0 ? style.returnEqWidgetActionPrimary : style.returnEqWidgetActionSecondary,
									{
										[style.returnEqWidgetActionWithArrow]: index === 0 && !!action.subAction,
										[style.returnEqWidgetActionExpanded]: expandedAction === action.name
									}
								)}
								onClick={() => {
									if ( !!action.subAction ) {
										setExpandedAction(prev => prev === action.name ? null : action.name);
										setShowRedirectList(false);
										return;
									}
									onActionClick(action.name);
								}}
							>
								{
									!!ActionIcon &&
									<span className={style.returnEqWidgetActionIcon}>
										<ActionIcon />
									</span>
								}
								<span className={style.returnEqWidgetActionText}>{action.title || action.name}</span>
								{
									index === 0 && !!action.subAction &&
									<span className={classNames(
										style.returnEqWidgetActionArrow,
										expandedAction === action.name && style.returnEqWidgetActionArrowOpen
									)}>
										<svg xmlns="http://www.w3.org/2000/svg" width="9" height="5" viewBox="0 0 9 5" fill="none">
										<path d="M0.000650406 -3.64262e-07L4.16732 4.16667L8.33398 0L0.000650406 -3.64262e-07Z" fill="white"/>
										</svg>
									</span>
								}
							</button>
							{/* Выпадающий список поддействий (например, "В порядке очереди" или "По номеру талона") */}
							{
								expandedAction === action.name &&
								!!action.subAction &&
								<div className={style.returnEqWidgetDropdown}>
									{
										action.subAction.map( subAction => {
											const SubActionIcon = subAction.icon;
											return (
											<button
												key={subAction.name}
												type="button"
												className={classNames("body-xsmall", style.returnEqWidgetDropdownItem)}
												onClick={() => onSubActionClick(subAction.name)}
											>
												{
													!!SubActionIcon &&
													<span className={style.returnEqWidgetActionIcon}>
														<SubActionIcon />
													</span>
												}
												<span>{subAction.title || subAction.name}</span>
											</button>
											);
										})
									}
								</div>
							}

							{/* Выпадающий список окон для перенаправления посетителя */}
							{
								action.name === "return-redirect" && showRedirectList &&
								<div className={style.returnEqWidgetDropdown}>
									{
										windowsStatus !== "loaded" &&
										<div className={classNames("body-xsmall", style.returnEqWidgetDropdownItem)}>Загрузка...</div>
									}
									{
										windowsStatus === "loaded" &&
										windowOptions.map( (windowItem) => (
											<button
												key={windowItem.id}
												type="button"
												className={classNames("body-xsmall", style.returnEqWidgetDropdownItem)}
												onClick={() => {
													setShowRedirectList(false);
													onAction("return-redirect-select", windowItem.id);
												}}
											>
												{windowItem.title}
											</button>
										))
									}
									{
										windowsStatus === "loaded" && windowOptions.length === 0 &&
										<div className={style.returnEqWidgetDropdownItem}>Нет доступных окон</div>
									}
								</div>
							}
						</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default ReturnWidget;
